import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  Alert,
  Backdrop,
  Button,
  Card,
  CircularProgress,
  CssBaseline,
  Grid,
  MenuItem,
  Snackbar,
  Typography,
} from '@mui/material';
import { withStyles } from '@mui/styles';
import { compose } from '@reduxjs/toolkit';
import { addDays, endOfDay, endOfMinute } from 'date-fns';
import localforage from 'localforage';
import _ from 'lodash';
import omitDeep from 'omit-deep-lodash';
import queryString from 'query-string';
import { useForm, useWatch } from 'react-hook-form';
import { connect } from 'react-redux';
import { Prompt, useLocation, useParams } from 'react-router-dom';
import { push } from 'redux-first-history';

import HistoryChart from '../../../components/HistoryChart';
import useFormPersist from '../../../components/custom-hooks/use-form-persist';
import usePrevious from '../../../components/custom-hooks/use-previous';
import ControllerTextField from '../../../components/react-hook-form-wrappers/controller-text-field';
import YesNoDialog from '../../../components/yes-no-dialog';
import {
  addAd,
  clearAdErrorMessage,
  clearAdMessage,
  clearEditingAd,
  getAdAwsS3UploadKit,
  getAdOccupiedPositions,
  loadAd,
  loadCategories,
  loadClients,
  unloadCategories,
  unloadClients,
  updateAd,
} from '../../../store/actions';
import {
  getAdsAvailablePositions,
  getAdsErrorMessage,
  getAdsMessage,
  getClients,
  getEditingAd,
  selectAwsS3UploadKitAd,
  selectCategories,
  selectCategoriesLoading,
  selectClientsLoading,
} from '../../../store/selectors';
import { formatDateTime } from '../../../utils';
import CheckboxAndDateTimePicker from '../../Assets/asset-detail/checkbox-and-date-time-range-picker';
import AdUploader from './ad-uploader';

const styles = (theme) => ({
  container: {},
  title: {
    marginBottom: theme.spacing(3),
    marginLeft: theme.spacing(1),
  },
  form: {
    width: '100%', // Fix IE 11 issue.
  },
  backdrop: {
    zIndex: 999,
    backgroundColor: 'rgba(255, 255, 255, 0.6)',
  },
  countdown: {
    display: 'flex',
    alignItems: 'center',
  },
});

let renderCount = 0;

const AD_TYPE_OPTIONS = [
  { id: 'portrait', name: 'Portrait' },
  { id: 'banner', name: 'Banner' },
];

const DEFAULT_AD_VALUES = {
  originalFileName: '',
  uploadedImages: [],
  preview: null,
};

const renderAdTypeOptions = () => {
  return AD_TYPE_OPTIONS.map((option) => {
    return (
      <MenuItem key={option.id} value={option.id}>
        {option.name}
      </MenuItem>
    );
  });
};

const renderCategoryOptions = (categories) => {
  return _.map(categories, (category) => {
    return (
      <MenuItem key={category.id} value={category.id}>
        {category.name}
      </MenuItem>
    );
  });
};

const renderClientOptions = (clients) => {
  return _.map(clients, (client) => {
    return (
      <MenuItem key={client.id} value={client.id}>
        {client.companyName}
      </MenuItem>
    );
  });
};

const renderPositionOptions = (availablePositions) => {
  return availablePositions.map((position) => {
    return (
      <MenuItem key={position} value={position}>
        {position}
      </MenuItem>
    );
  });
};

const PositionField = ({ control, availablePositions }) => {
  const isPublished = useWatch({ control, name: 'isPublished' });

  if (!isPublished) {
    return null;
  }

  return (
    <Grid item xs={12}>
      <ControllerTextField
        select
        control={control}
        label="Position"
        name="position"
        placeholder="Choose a position"
      >
        {renderPositionOptions(availablePositions)}
      </ControllerTextField>
    </Grid>
  );
};

const AdDetail = ({
  classes,
  initialValues,
  loadAd,
  clearEditingAd,
  unloadClients,
  unloadCategories,
  loadCategories,
  loadClients,
  getAdOccupiedPositions,
  getAdAwsS3UploadKit,
  isProcessing,
  categories,
  clients,
  editingAd,
  awsS3UploadKit,
  availablePositions,
  push,
  clearAdErrorMessage,
  adErrorMessage,
  addAd,
  updateAd,
}) => {
  const { adId } = useParams();
  const { search: locationSearch } = useLocation();
  const isEditing = useMemo(() => !!adId && adId !== 'create', [adId]);
  const adTypeFromQuery = useMemo(() => {
    const queryAdType = queryString.parse(locationSearch).adType;
    return AD_TYPE_OPTIONS.some(({ id }) => id === queryAdType)
      ? queryAdType
      : null;
  }, [locationSearch]);
  const previousInitialValues = usePrevious(initialValues);

  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [successfullySavedAdId, setSuccessfullySavedAdId] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const isSubmittingRef = useRef(false);

  // Remove items from localforage that are older than 10 days
  useEffect(() => {
    const now = Date.now();
    const tenDays = 10 * 24 * 60 * 60 * 1000;
    localforage
      .iterate((value, key) => {
        const { __timestamp } = value;
        if (now - __timestamp >= tenDays) {
          localforage.removeItem(key);
        }
      })
      .then(() => {
        // ignore
      })
      .catch((err) => {
        console.log('[localforage.iterate] Error:', err);
      });
  }, []);

  useEffect(() => {
    loadCategories({ pageSize: 100 });
    loadClients({ pageSize: 300 });
    if (isEditing) {
      getAdOccupiedPositions({ adId });
      getAdAwsS3UploadKit({ adId });
      loadAd(adId).then(() => {
        setIsSaving(false);
      });
    }

    renderCount = 0;

    return () => {
      clearEditingAd();
      unloadClients();
      unloadCategories();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adId]);

  renderCount += 1;

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    reset,
    formState: { isDirty, isSubmitting },
  } = useForm({
    defaultValues: {
      id: '',
      adType: 'portrait',
      categoryId: '',
      clientId: '',
      sourceUrl: '',
      description: '',
      position: '',
      isPublished: false,
      cutOffDate: endOfDay(addDays(new Date(), 28)),
      sourceUrlCount: 0,
      createdAt: '',
      updatedAt: '',
      ...DEFAULT_AD_VALUES,
    },
  });

  const { clearPersist, stopPersistInterval } = useFormPersist({
    name: adId ? `ad-details-form-${adId}` : 'ad-details-form',
    control,
    getValues,
    reset,
    onDataRestored: () => {
      setShowRestoreDialog(true);
    },
    timeout: 6 * 24 * 60 * 60 * 1000, // 6 days
    exclude: [
      'createdAt',
      'updatedAt',
      'isPublished',
      'cutOffDate',
      'sourceUrlCount',
    ],
  });

  useEffect(() => {
    if (
      (initialValues && initialValues.id !== previousInitialValues?.id) ||
      successfullySavedAdId
    ) {
      setSuccessfullySavedAdId(null);
      const valuesToReset =
        !isEditing && adTypeFromQuery
          ? { ...initialValues, adType: adTypeFromQuery }
          : initialValues;
      reset(valuesToReset);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues, reset]);

  useEffect(() => {
    if (successfullySavedAdId) {
      clearPersist();
      push('/dashboard/ads?prompt=no');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successfullySavedAdId]);

  const watchedCategoryId = useWatch({ control, name: 'categoryId' });
  const watchedAdType = useWatch({ control, name: 'adType' });

  useEffect(() => {
    if (isEditing || !watchedCategoryId || !watchedAdType) {
      return;
    }
    getAdOccupiedPositions({
      categoryId: watchedCategoryId,
      adType: watchedAdType,
    });
  }, [isEditing, watchedCategoryId, watchedAdType, getAdOccupiedPositions]);

  const validateUploadedAd = (data) => {
    if (!data.originalFileName) {
      setErrorMessage('Please upload an image');
      return false;
    }

    if (data.localOriginalUrl && data.uploadedImages.length !== 6) {
      setErrorMessage('Please crop the ad');
      return false;
    }

    if (!data.categoryId) {
      setErrorMessage('Please choose a category');
      return false;
    }

    if (!data.clientId) {
      setErrorMessage('Please choose a client');
      return false;
    }

    if (!data.sourceUrl) {
      setErrorMessage('Please enter the source URL');
      return false;
    }

    if (data.isPublished && !data.position) {
      setErrorMessage('Please choose a position');
      return false;
    }

    return true;
  };

  const onSubmit = async (data) => {
    if (isSubmittingRef.current) {
      return;
    }

    isSubmittingRef.current = true;

    setErrorMessage(null);
    clearAdErrorMessage();

    if (!validateUploadedAd(data)) {
      isSubmittingRef.current = false;
      return;
    }

    setIsSaving(true);

    const formValues = omitDeep(data, [
      'croppedImageUrl',
      'localOriginalUrl',
      'temp',
      'uploadedImages',
      'originalFileName',
      'createdAt',
      'updatedAt',
      'sourceUrlCount',
      'cutOffDate',
    ]);

    if (!_.isEmpty(data.uploadedImages)) {
      const img = {};
      _.forEach(data.uploadedImages, (e) => {
        img[e.key] = { name: e.name, type: e.type };
      });
      formValues.img = img;
    }

    if (data.preview) {
      formValues.preview = data.preview;
    }

    if (data.isPublished) {
      if (data.cutOffDate) {
        formValues.cutOffTime = endOfMinute(data.cutOffDate).getTime();
      }
    } else {
      formValues.cutOffTime = 0;
    }

    // Strip empty strings so optional server-side Joi validators don't reject them
    _.forEach(formValues, (value, key) => {
      if (value === '' || value === null) {
        delete formValues[key];
      }
    });

    try {
      let response;
      if (isEditing) {
        response = await updateAd(formValues);
      } else {
        response = await addAd(formValues);
      }

      if (!adErrorMessage && !errorMessage) {
        setSuccessfullySavedAdId(response?.ad?.id || adId || 'created');
      }
    } catch (error) {
      setIsSaving(false);
    }

    isSubmittingRef.current = false;
  };

  const onCancel = () => {
    reset(initialValues);
    push('/dashboard/ads?prompt=no');
  };

  return (
    <>
      <CssBaseline />
      <Grid className={classes.container}>
        <Grid container className={classes.title}>
          <Typography component="h1" variant="h6">
            {isEditing ? 'Edit' : 'Create New'} Ad
          </Typography>
        </Grid>
        <Grid container direction="row" justifyContent="center">
          <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
            <Grid container item xs={12} direction="column">
              <Grid
                container
                justifyContent="space-around"
                direction={{ xs: 'row', md: 'row-reverse' }}
                style={{ paddingBottom: 24 }}
              >
                <AdUploader
                  control={control}
                  isEditing={isEditing}
                  getValues={getValues}
                  setValue={setValue}
                  awsS3UploadKit={awsS3UploadKit}
                  initialRequestOptions={editingAd?.requestOptions}
                  showOriginalImage
                  watchAdTypeField="adType"
                />
              </Grid>
              <Grid
                container
                justifyContent="space-around"
                direction={{ xs: 'row', md: 'row-reverse' }}
                style={{ paddingBottom: 24 }}
              >
                <Grid item xs={12} md={4} />
                <Grid
                  component={Card}
                  container
                  item
                  xs={12}
                  md={7}
                  gap={3}
                  style={{ padding: 24 }}
                >
                  <Grid item xs={12}>
                    Render count: {renderCount}
                  </Grid>
                  {isEditing && (
                    <Grid item xs={12}>
                      <ControllerTextField
                        control={control}
                        label="Ad ID"
                        name="id"
                        showCopyButton
                        required={false}
                        disabled
                      />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <ControllerTextField
                      select
                      control={control}
                      label="Type"
                      name="adType"
                      placeholder="Choose an ad type"
                      disabled
                    >
                      {renderAdTypeOptions()}
                    </ControllerTextField>
                  </Grid>
                  <Grid item xs={12}>
                    <ControllerTextField
                      select
                      control={control}
                      label="Category"
                      name="categoryId"
                      placeholder="Choose a category"
                      disabled={isEditing}
                    >
                      {renderCategoryOptions(categories)}
                    </ControllerTextField>
                  </Grid>
                  <Grid item xs={12}>
                    <ControllerTextField
                      select
                      control={control}
                      label="Client"
                      name="clientId"
                      placeholder="Choose a client"
                      disabled={isEditing}
                    >
                      {renderClientOptions(clients)}
                    </ControllerTextField>
                  </Grid>
                  <Grid item xs={12}>
                    <ControllerTextField
                      control={control}
                      label="Source URL"
                      name="sourceUrl"
                      placeholder="Enter the original source URL"
                      showCopyButton
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ControllerTextField
                      control={control}
                      label="Description"
                      name="description"
                      placeholder="Enter Description"
                      multiline
                      countWords
                      showCopyButton
                      required={false}
                    />
                  </Grid>
                  {isEditing && (
                    <Grid container item xs={12} spacing={3}>
                      <Grid item xs={12}>
                        <ControllerTextField
                          control={control}
                          label="Source URL Clicks"
                          name="sourceUrlCount"
                          disabled
                          required={false}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <ControllerTextField
                          control={control}
                          label="Created At"
                          name="createdAt"
                          disabled
                          required={false}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <ControllerTextField
                          control={control}
                          label="Updated At"
                          name="updatedAt"
                          disabled
                          required={false}
                        />
                      </Grid>
                    </Grid>
                  )}
                  <CheckboxAndDateTimePicker
                    control={control}
                    getValues={getValues}
                    setValue={setValue}
                    checkboxLabel="Published"
                    checkboxName="isPublished"
                    endDateLabel="Cut-off At"
                    endDateName="cutOffDate"
                  />
                  <PositionField
                    control={control}
                    availablePositions={availablePositions}
                  />
                  {isEditing && (
                    <Grid item xs={12}>
                      <HistoryChart
                        publishHistory={editingAd?.publishHistory}
                        showPosition
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
            <Grid
              container
              item
              justifyContent="space-around"
              direction={{ xs: 'row', md: 'row-reverse' }}
            >
              <Grid item xs={12} md={4} />
              <Grid container item xs={12} md={7}>
                <Grid
                  container
                  item
                  xs={12}
                  justifyContent="center"
                  columnGap={3}
                >
                  <Button
                    color="primary"
                    disabled={isSubmitting || !isDirty || isSaving}
                    type="submit"
                    variant="contained"
                  >
                    {isEditing ? 'Save' : 'Submit'}
                  </Button>
                  <Button type="button" variant="contained" onClick={onCancel}>
                    {isDirty ? 'Cancel' : 'Go Back To Ad List'}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </form>
        </Grid>
      </Grid>
      {showRestoreDialog && (
        <YesNoDialog
          open={showRestoreDialog}
          title="Restore?"
          message="There are unsaved changes from last edit. Do you want to restore the changes?"
          primaryButtonText="Yes"
          onPrimaryButtonClick={() => {
            setShowRestoreDialog(false);
          }}
          secondaryButtonText="No"
          onSecondaryButtonClick={() => {
            setShowRestoreDialog(false);
            setTimeout(() => {
              clearPersist();
              reset(initialValues);
            }, 100);
          }}
        />
      )}
      <Prompt
        when={isDirty && !successfullySavedAdId}
        message={(location, action) => {
          if (action === 'PUSH' && location.search.includes('prompt=no')) {
            return true;
          }

          const userResponse = window.confirm(
            'There are unsaved changes. Do you wish to discard them?'
          );

          if (userResponse) {
            stopPersistInterval();
            clearPersist();
          }

          return userResponse;
        }}
      />
      <Prompt
        when={!isDirty || !!successfullySavedAdId}
        message={() => {
          stopPersistInterval();
          clearPersist();
          return true;
        }}
      />
      <Snackbar
        autoHideDuration={5000}
        onClose={() => {
          setErrorMessage(null);
          clearAdErrorMessage();
        }}
        open={!!adErrorMessage || !!errorMessage}
      >
        <Alert
          severity="error"
          action={
            <Button
              size="small"
              color="secondary"
              onClick={() => {
                setErrorMessage(null);
                clearAdErrorMessage();
              }}
            >
              Dismiss
            </Button>
          }
        >
          {adErrorMessage || errorMessage}
        </Alert>
      </Snackbar>
      <Backdrop className={classes.backdrop} open={isProcessing || isSaving}>
        <CircularProgress />
      </Backdrop>
    </>
  );
};

const mapStateToProps = (state) => {
  const clientsLoading = selectClientsLoading(state);
  const categoriesLoading = selectCategoriesLoading(state);
  const adsLoading = state.ads.loading;
  const isProcessing = adsLoading || clientsLoading || categoriesLoading;

  const editingAd = getEditingAd(state);
  const categories = selectCategories(state);
  const clients = getClients(state);
  const adErrorMessage = getAdsErrorMessage(state);
  const message = getAdsMessage(state);
  const awsS3UploadKit = selectAwsS3UploadKitAd(state);
  const availablePositions = getAdsAvailablePositions(
    state,
    editingAd.position
  );

  let initialValues = null;
  if (!_.isEmpty(editingAd)) {
    initialValues = {
      id: editingAd.id || '',
      adType: editingAd.adType || 'banner',
      categoryId: editingAd.category?.id || '',
      clientId: editingAd.client?.id || '',
      description: editingAd.description || '',
      sourceUrl: editingAd.sourceUrl || '',
      sourceUrlCount: editingAd.sourceUrlCount || 0,
      position: editingAd.position || '',
      isPublished: editingAd.publishTime > 0,
      cutOffDate:
        editingAd.cutOffTime > 0
          ? new Date(editingAd.cutOffTime)
          : endOfDay(addDays(new Date(), state.ads.preferences.cutOffTimeDs)),
      createdAt: formatDateTime(editingAd.createdAt),
      updatedAt: formatDateTime(editingAd.updatedAt),
      originalFileName: editingAd.image?.original?.name || '',
      uploadedImages: [],
      preview: null,
    };
  } else if (!isProcessing) {
    initialValues = {
      id: '',
      adType: 'portrait',
      categoryId: '',
      clientId: '',
      description: '',
      sourceUrl: '',
      sourceUrlCount: 0,
      position: '',
      isPublished: false,
      cutOffDate: endOfDay(
        addDays(new Date(), state.ads.preferences.cutOffTimeDs)
      ),
      createdAt: '',
      updatedAt: '',
      ...DEFAULT_AD_VALUES,
    };
  }

  return {
    adErrorMessage,
    message,
    isProcessing,
    categories,
    clients,
    initialValues,
    editingAd,
    awsS3UploadKit,
    availablePositions,
  };
};

export default compose(
  connect(mapStateToProps, {
    loadAd,
    loadCategories,
    loadClients,
    getAdOccupiedPositions,
    getAdAwsS3UploadKit,
    push,
    addAd,
    updateAd,
    clearEditingAd,
    clearAdErrorMessage,
    clearAdMessage,
    unloadClients,
    unloadCategories,
  }),
  withStyles(styles)
)(AdDetail);
